/**
 * The platform's number format: thousands separators and exactly two decimal places.
 *
 * Every money and credit figure reads the same way — 1,234.50, never 1234.5 and never 1,234. It was
 * written inline in a dozen accounting components before this; import it rather than repeating the
 * options object, so a change of convention is one edit.
 */
export const DECIMAL_2 = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
} as const;

/** Formats a number for display. Nothing is rounded away — two places is the stored precision. */
export function formatAmount(n: number): string {
  return n.toLocaleString("en-US", DECIMAL_2);
}

/** Same, for the decimal STRINGS the API returns. Non-numeric input yields an em dash. */
export function formatAmountString(v: string | null | undefined): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? formatAmount(n) : "—";
}

/**
 * Strips everything that isn't part of a number, keeps ONE decimal point and at most two places
 * after it. Used by amount inputs so "1.2.3" and "1.239" cannot be typed, rather than being
 * rejected after the fact — and so a pasted "1,234.56" is understood.
 */
export function sanitizeAmountInput(raw: string): string {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  const [whole = "", ...rest] = cleaned.split(".");
  return rest.length > 0 ? `${whole}.${rest.join("").slice(0, 2)}` : whole;
}

/** Same as `formatAmount`, prefixed with "$"; the minus sign goes before the "$", not after. */
export function formatUsd(v: number | string | null | undefined): string {
  if (v == null || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  // Decide the sign from the rounded magnitude so -0.001 doesn't display as "-$0.00".
  const magnitude = formatAmount(Math.abs(n));
  const sign = n < 0 && magnitude !== "0.00" ? "-" : "";
  return `${sign}$${magnitude}`;
}

/** "before → after" as two `formatUsd` amounts; a missing side reads as an em dash. */
export function formatUsdChange(
  before: number | string | null | undefined,
  after: number | string | null | undefined,
): string {
  return `${formatUsd(before)} → ${formatUsd(after)}`;
}

/**
 * A 0..1 rate as whole percent — the ONE rendering a table cell and its CSV export both use, so
 * the exported file can never disagree with the row it came from. Null/undefined is an empty cell.
 */
export function formatRate(value: number | null | undefined): string {
  return value == null ? "" : `${Math.round(value * 100)}%`;
}
